import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { DestinationCGType, SurveyCGType, SurveyStatusCGType } from '@/types/graphql/root';
import { useParams, useRouter } from 'next/navigation';
import { alertModalAtom, confirmModalAtom } from '@/atom/modal';
import { filterNullUndefined, getSortedDestinationList } from '@/utils';
import { completeTrip, setItineraryOpened } from '@/utils/actions';
import { surveyAtom } from '@/atom/survey';

const useAutofillTrip = ({
  tripData,
  setTripData,
  setDestinations,
  setTopSlot,
  setTopDestInMap,
  isLoggedin,
}: {
  tripData: SurveyCGType | null;
  setTripData: React.Dispatch<React.SetStateAction<SurveyCGType | null>>;
  setDestinations: React.Dispatch<React.SetStateAction<DestinationCGType[]>>;
  setTopSlot: React.Dispatch<React.SetStateAction<DestinationCGType | null>>;
  setTopDestInMap: (topDest: DestinationCGType | null) => void;
  isLoggedin: boolean;
}) => {
  const router = useRouter();
  const { id: tripId } = useParams();
  const [survey, setSurvey] = useAtom(surveyAtom);

  const setConfirmModal = useSetAtom(confirmModalAtom);
  const setAlertModal = useSetAtom(alertModalAtom);

  const [isAlreadyLoaded, setIsAlreadyLoaded] = useState(false);

  useEffect(() => {
    setSurvey({
      fromDateTime: undefined,
      toDateTime: undefined,
      companion: undefined,
      zones: [],
      customSlots: [],
      departurePoint: undefined,
    });
  }, []);

  useEffect(() => {
    if (!tripData) return;

    const isOwnTrip = tripData?.isOwn ?? false;
    const isOpenedTrip = tripData?.status === SurveyStatusCGType.OpenedCGType;
    const autofillAvailable = tripData?.autofillAvailable ?? false;
    const isFailedNama = tripData.status === SurveyStatusCGType.FailedCGType;

    if (!isAlreadyLoaded) {
      setIsAlreadyLoaded(true);
      const destinations = tripData?.itinerary?.destinations;
      if (destinations) {
        const sortedDestinations = getSortedDestinationList(filterNullUndefined(destinations));
        setDestinations(sortedDestinations);
        setTopSlot(sortedDestinations?.[0] ?? null);
        setTopDestInMap(sortedDestinations?.[0] ?? null);
      }
    }

    if (isOwnTrip && !isOpenedTrip && autofillAvailable && tripId && !isFailedNama) {
      if (window.sessionStorage.getItem('allthattravel_survey')) {
        setConfirmModal({ visible: false });
        setItineraryOpened(tripId as string);
        setTripData((prevTrip) => {
          if (!prevTrip) return null;
          const newTrip = { ...prevTrip, autofillAvailable: false };
          return newTrip;
        });
        router.replace(`/trip/loading/autofill/${tripId as string}`);
        return;
      }
      openAutofillQuestionModal(tripId as string);
    }

    if (autofillAvailable && isFailedNama) {
      setAlertModal({
        visible: true,
        title: '알림',
        message: '일정표 최적화에 실패했어요.\n일정표를 직접 편집하여 일정을 조정할 수 있어요.',
        onConfirm: () => {
          setAlertModal({ visible: false });
        },
        onOuterClick: () => {
          setAlertModal({ visible: false });
        },
      });
    }
  }, [tripData, isLoggedin]);

  const openAutofillQuestionModal = useCallback(
    (surveyId: string) => {
      setConfirmModal({
        visible: true,
        title: '일정표를 채워드릴까요?',
        message: '동선과 영업 시간을 고려하여\n효율적인 일정표를 완성해드릴게요.',
        onClose: async () => {
          const isAllThatTravelSurvey = window.sessionStorage.getItem('allthattravel_survey');
          if (isAllThatTravelSurvey) {
            window.alert(
              '힌트를 얻으려면 AI가 일정표를 먼저 채워야 해요.\n직접 플래너를 만들 땐, 자유롭게 일정표를 채울 수 있어요!',
            );
            return;
          }

          setConfirmModal({ visible: false });
          setItineraryOpened(surveyId);
          completeTrip(surveyId, true);
          setTripData((prevTrip) => {
            if (!prevTrip) return null;
            const newTrip = { ...prevTrip, autofillAvailable: false, status: SurveyStatusCGType.OpenedCGType };
            return newTrip;
          });
        },
        onConfirm: async () => {
          setConfirmModal({ visible: false });
          setItineraryOpened(surveyId);
          setTripData((prevTrip) => {
            if (!prevTrip) return null;
            const newTrip = { ...prevTrip, autofillAvailable: false };
            return newTrip;
          });
          router.replace(`/trip/loading/autofill/${surveyId}`);
        },
      });
    },
    [setConfirmModal, tripId],
  );
};

export default useAutofillTrip;
